import React, { useState, useEffect, useMemo } from 'react';
import {
  Users,
  UserPlus,
  ShieldAlert,
  ShieldCheck,
  KeyRound,
  Copy,
  Trash2,
  RefreshCw,
  CheckCircle2,
  X,
  Phone,
  Mail,
  Eye,
  EyeOff,
  Search,
  AlertCircle,
  Settings,
  Check,
} from 'lucide-react';
import { StaffAccount } from '../../types';
import { staffService, CreateStaffInput } from '../../services/staffService';
import { useAuth } from '../../auth/AuthContext';
import { AdminLoadMore } from './AdminLoadMore';
import './AdminPages.css';

export interface SystemSectionDef {
  key: string;
  name: string;
  category: 'OPERATIONAL' | 'STUDENTS' | 'FLEET' | 'FINANCIAL' | 'SECURITY';
  categoryLabel: string;
  description: string;
}

export const ALL_SYSTEM_SECTIONS: SystemSectionDef[] = [
  // 1. العمليات والتشغيل
  {
    key: 'DASHBOARD',
    name: 'لوحة القيادة (Dashboard)',
    category: 'OPERATIONAL',
    categoryLabel: 'العمليات والتشغيل',
    description: 'متابعة إحصائيات الرحلات اليومية والحجوزات والمقاعد الشاغرة',
  },
  {
    key: 'BOOKINGS',
    name: 'إدارة الحجوزات والعمليات',
    category: 'OPERATIONAL',
    categoryLabel: 'العمليات والتشغيل',
    description: 'استعراض التذاكر، الكلندر، وتأكيد حجوزات الطلاب',
  },
  {
    key: 'TRIPS',
    name: 'جدول الرحلات والمواعيد',
    category: 'OPERATIONAL',
    categoryLabel: 'العمليات والتشغيل',
    description: 'إضافة ومتابعة مواعيد انطلاق الحافلات والمانيفست',
  },
  {
    key: 'DEMAND',
    name: 'طلبات الرحلات وتحليلات الطلب',
    category: 'OPERATIONAL',
    categoryLabel: 'العمليات والتشغيل',
    description: 'متابعة طلبات الطلاب للرحلات المباشرة واستبيانات الطلب',
  },

  // 2. شؤون الطلاب
  {
    key: 'STUDENTS',
    name: 'شؤون الطلاب والمسارات',
    category: 'STUDENTS',
    categoryLabel: 'شؤون الطلاب',
    description: 'استعراض بيانات الطلاب وتعديل محطات الركوب والمسارات',
  },
  {
    key: 'CREATE_STUDENT',
    name: 'إنشاء حسابات الطلاب (واتساب)',
    category: 'STUDENTS',
    categoryLabel: 'شؤون الطلاب',
    description: 'تسجيل حساب جديد للطالب وإرسال بيانات الدخول عبر الواتساب',
  },
  {
    key: 'PROMO_REWARDS',
    name: 'البروموكود ومكافآت الطلاب',
    category: 'STUDENTS',
    categoryLabel: 'شؤون الطلاب',
    description: 'إنشاء أكواد الخصم ونقاط ومكافآت رحلات الطلاب',
  },

  // 3. الأسطول والجامعات
  {
    key: 'UNIVERSITIES',
    name: 'الجامعات والكليات',
    category: 'FLEET',
    categoryLabel: 'الأسطول والجامعات',
    description: 'إدارة الجامعات ومواقع النزول ومحافظات الجامعات',
  },
  {
    key: 'VEHICLES',
    name: 'المركبات والأسطول',
    category: 'FLEET',
    categoryLabel: 'الأسطول والجامعات',
    description: 'إدارة أرقام الحافلات وأسماء السائقين وسعة الحافلة',
  },

  // 4. المالية والخزينة
  {
    key: 'PAYMENTS',
    name: 'اعتماد المدفوعات والتحويلات',
    category: 'FINANCIAL',
    categoryLabel: 'المالية والخزينة',
    description: 'مراجعة صور إيصالات إنستاباي والمحافظ واعتمادها',
  },
  {
    key: 'TREASURY',
    name: 'الخزينة والتعاملات المالية',
    category: 'FINANCIAL',
    categoryLabel: 'المالية والخزينة',
    description: 'كشف حركة الخزينة، الإيداعات، والمصروفات والأرباح',
  },
  {
    key: 'PAYMENT_SETTINGS',
    name: 'إعدادات وسائل الدفع',
    category: 'FINANCIAL',
    categoryLabel: 'المالية والخزينة',
    description: 'تعديل أرقام إنستاباي، المحافظ الإلكترونية، وبيانات الفرع',
  },

  // 5. النظام والرقابة
  {
    key: 'NOTIFICATIONS',
    name: 'الإشعارات الجماعية',
    category: 'SECURITY',
    categoryLabel: 'النظام والرقابة',
    description: 'إرسال تنبيهات جماعية لكافة الطلاب على المنظومة',
  },
  {
    key: 'AUDIT_LOGS',
    name: 'سجل التدقيق والرقابة (Audit)',
    category: 'SECURITY',
    categoryLabel: 'النظام والرقابة',
    description: 'مراقبة كافة الحركات الإدارية والأمان ومن قام بكل تعديل',
  },
  {
    key: 'SETTINGS',
    name: 'الإعدادات العامة للنظام',
    category: 'SECURITY',
    categoryLabel: 'النظام والرقابة',
    description: 'إعدادات المنظومة الأساسية وضبط التشغيل العام',
  },
];

export const DEFAULT_OPERATOR_SECTIONS: string[] = [
  'DASHBOARD',
  'BOOKINGS',
  'TRIPS',
  'DEMAND',
  'STUDENTS',
  'CREATE_STUDENT',
  'UNIVERSITIES',
  'VEHICLES',
  'NOTIFICATIONS',
];

export const AdminStaffManagement: React.FC = () => {
  const { user } = useAuth();
  const [staffList, setStaffList] = useState<StaffAccount[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [roleFilter, setRoleFilter] = useState<string>('ALL');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [visibleCount, setVisibleCount] = useState<number>(15);

  // Modals state
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [newStaff, setNewStaff] = useState<CreateStaffInput>({
    fullName: '',
    username: '',
    phoneNumber: '',
    password: '',
    role: 'OPERATOR',
    allowedSections: [...DEFAULT_OPERATOR_SECTIONS],
  });
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Edit / Password reset state
  const [editingStaff, setEditingStaff] = useState<StaffAccount | null>(null);
  const [newPasswordVal, setNewPasswordVal] = useState<string>('');
  const [isResetModalOpen, setIsResetModalOpen] = useState<boolean>(false);

  // Individual Section Permissions State
  const [permissionStaff, setPermissionStaff] = useState<StaffAccount | null>(null);
  const [selectedSections, setSelectedSections] = useState<string[]>([]);
  const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState<boolean>(false);
  const [isSavingPermissions, setIsSavingPermissions] = useState<boolean>(false);

  // Notification message
  const [notification, setNotification] = useState<{
    text: string;
    type: 'success' | 'error' | 'info';
  } | null>(null);

  // Load staff
  const loadStaff = async (force = false) => {
    setIsLoading(true);
    try {
      const data = await staffService.getAllStaff(force);
      setStaffList(data);
    } catch (err) {
      console.warn('Failed to fetch staff accounts:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadStaff();
  }, []);

  const showToast = (text: string, type: 'success' | 'error' | 'info' = 'success') => {
    setNotification({ text, type });
    setTimeout(() => {
      setNotification(null);
    }, 4000);
  };

  // Filtered staff
  const filteredStaff = useMemo(() => {
    return staffList.filter((s) => {
      const matchSearch =
        !searchTerm.trim() ||
        s.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (s.phoneNumber && s.phoneNumber.includes(searchTerm.trim()));

      const matchRole = roleFilter === 'ALL' || s.role === roleFilter;
      const matchStatus = statusFilter === 'ALL' || s.status === statusFilter;

      return matchSearch && matchRole && matchStatus;
    });
  }, [staffList, searchTerm, roleFilter, statusFilter]);

  // Reset pagination on filter change
  useEffect(() => {
    setVisibleCount(15);
  }, [searchTerm, roleFilter, statusFilter]);

  const visibleStaff = useMemo(() => {
    return filteredStaff.slice(0, visibleCount);
  }, [filteredStaff, visibleCount]);


  // Handle Add Staff
  const handleCreateStaff = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newStaff.fullName.trim() || !newStaff.username.trim()) {
      showToast('يرجى كتابة اسم الموظف واسم المستخدم', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const created = await staffService.createStaff({
        ...newStaff,
        createdBy: user?.email || 'مدير المنظومة',
      });
      setStaffList((prev) => [created, ...prev.filter((item) => item.uid !== created.uid)]);
      setIsAddModalOpen(false);
      setNewStaff({
        fullName: '',
        username: '',
        phoneNumber: '',
        password: '',
        role: 'OPERATOR',
      });
      showToast(`تم إنشاء حساب الموظف (${created.fullName}) بنجاح!`, 'success');
    } catch (err: any) {
      showToast(err?.message || 'حدث خطأ أثناء إنشاء الحساب', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Toggle Status (ACTIVE <-> SUSPENDED)
  const handleToggleStatus = async (staff: StaffAccount) => {
    const nextStatus = staff.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE';
    const confirmMsg =
      nextStatus === 'SUSPENDED'
        ? `هل أنت متأكد من تجميد حساب الموظف (${staff.fullName})؟ لن يتمكن من فتح لوحة التحكم بعد الآن.`
        : `هل ترغب في إعادة تنشيط حساب الموظف (${staff.fullName})؟`;

    if (!window.confirm(confirmMsg)) return;

    try {
      await staffService.toggleStaffStatus(staff.uid, nextStatus);
      setStaffList((prev) =>
        prev.map((s) => (s.uid === staff.uid ? { ...s, status: nextStatus } : s))
      );
      showToast(
        nextStatus === 'SUSPENDED' ? 'تم تجميد حساب الموظف' : 'تم إعادة تنشيط الحساب',
        'info'
      );
    } catch (err: any) {
      showToast(err?.message || 'فشل تحديث حالة الحساب', 'error');
    }
  };

  // Reset Password
  const handleSaveNewPassword = async () => {
    if (!editingStaff || !newPasswordVal.trim()) {
      showToast('يرجى إدخال كلمة المرور الجديدة', 'error');
      return;
    }
    try {
      await staffService.changeStaffPassword(editingStaff.uid, newPasswordVal.trim());
      setStaffList((prev) =>
        prev.map((s) =>
          s.uid === editingStaff.uid ? { ...s, password: newPasswordVal.trim() } : s
        )
      );
      setIsResetModalOpen(false);
      setEditingStaff(null);
      setNewPasswordVal('');
      showToast('تم تحديث كلمة المرور بنجاح!', 'success');
    } catch (err: any) {
      showToast(err?.message || 'فشل تحديث كلمة المرور', 'error');
    }
  };

  // Delete Staff
  const handleDeleteStaff = async (staff: StaffAccount) => {
    if (staff.role === 'SUPER_ADMIN') {
      showToast('لا يمكن حذف حساب المشرف العام من هنا', 'error');
      return;
    }
    if (
      !window.confirm(
        `تحذير: هل أنت متأكد تماماً من حذف حساب (${staff.fullName}) نهائياً من النظام؟`
      )
    ) {
      return;
    }

    try {
      await staffService.deleteStaff(staff.uid);
      setStaffList((prev) => prev.filter((s) => s.uid !== staff.uid));
      showToast(`تم حذف حساب (${staff.fullName}) بنجاح`, 'info');
    } catch (err: any) {
      showToast(err?.message || 'فشل حذف الحساب', 'error');
    }
  };

  // Copy WhatsApp Handover Message
  const handleCopyWhatsAppMessage = (staff: StaffAccount) => {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://otostudents.com';
    const message = `مرحباً بك في فريق عمل OTO STUDENTS! 🚀\n` +
      `تم إنشاء وتفعيل حساب التشغيل الخاص بك:\n\n` +
      `👤 الاسم: ${staff.fullName}\n` +
      `🔑 اسم المستخدم: ${staff.username}\n` +
      `🔒 كلمة المرور: ${staff.password || 'OtoStaff@2026'}\n` +
      `🌐 رابط لوحة التحكم: ${origin}/login\n\n` +
      `📌 الصلاحيات: إدارة التشغيل والحجوزات ومتابعة شؤون الطلاب والأسطول.\n` +
      `بالتوفيق في خدمة الطلاب! ✨`;

    navigator.clipboard.writeText(message);
    showToast('تم نسخ رسالة الواتساب إلى الحافظة بنجاح!', 'success');
  };

  // Open Individual Section Permissions Modal
  const handleOpenPermissionsModal = (staff: StaffAccount) => {
    setPermissionStaff(staff);
    const initialSections =
      staff.allowedSections && staff.allowedSections.length > 0
        ? [...staff.allowedSections]
        : staff.role === 'SUPER_ADMIN'
        ? ALL_SYSTEM_SECTIONS.map((s) => s.key)
        : [...DEFAULT_OPERATOR_SECTIONS];
    setSelectedSections(initialSections);
    setIsPermissionsModalOpen(true);
  };

  // Toggle single section permission
  const handleToggleSection = (sectionKey: string) => {
    setSelectedSections((prev) =>
      prev.includes(sectionKey)
        ? prev.filter((k) => k !== sectionKey)
        : [...prev, sectionKey]
    );
  };

  // Select all sections
  const handleSelectAllSections = () => {
    setSelectedSections(ALL_SYSTEM_SECTIONS.map((s) => s.key));
  };

  // Set default operational sections
  const handleSetDefaultOperatorSections = () => {
    setSelectedSections([...DEFAULT_OPERATOR_SECTIONS]);
  };

  // Clear all sections
  const handleClearAllSections = () => {
    setSelectedSections([]);
  };

  // Save permissions to Firestore and update state
  const handleSavePermissions = async () => {
    if (!permissionStaff) return;
    setIsSavingPermissions(true);
    try {
      await staffService.updateStaff(permissionStaff.uid, {
        allowedSections: selectedSections,
      });
      setStaffList((prev) =>
        prev.map((s) =>
          s.uid === permissionStaff.uid
            ? { ...s, allowedSections: selectedSections }
            : s
        )
      );
      setIsPermissionsModalOpen(false);
      showToast(
        `تم حفظ وتحديث صلاحيات الأقسام للموظف (${permissionStaff.fullName}) بنجاح!`,
        'success'
      );
    } catch (err: any) {
      showToast(err?.message || 'فشل حفظ الصلاحيات', 'error');
    } finally {
      setIsSavingPermissions(false);
    }
  };

  return (
    <div className="admin-staff-management-container" dir="rtl" style={{ padding: '1.5rem', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Toast Alert */}
      {notification && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            padding: '12px 24px',
            borderRadius: '12px',
            background:
              notification.type === 'success'
                ? '#10B981'
                : notification.type === 'error'
                ? '#EF4444'
                : '#3B82F6',
            color: '#fff',
            fontWeight: '600',
            boxShadow: '0 10px 25px -5px rgba(0,0,0,0.2)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          {notification.type === 'success' ? (
            <CheckCircle2 size={18} />
          ) : (
            <AlertCircle size={18} />
          )}
          <span>{notification.text}</span>
        </div>
      )}

      {/* Header Banner */}
      <div
        style={{
          background: 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)',
          borderRadius: '20px',
          padding: '2rem',
          color: '#fff',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1.5rem',
          boxShadow: '0 10px 25px -5px rgba(15, 23, 42, 0.3)',
          marginBottom: '2rem',
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
            <span
              style={{
                background: 'rgba(229, 35, 42, 0.2)',
                color: '#FF6B6B',
                padding: '4px 12px',
                borderRadius: '8px',
                fontSize: '12px',
                fontWeight: '700',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <Users size={14} />
              صلاحيات الإدارة والتشغيل
            </span>
            <span style={{ fontSize: '13px', color: '#94A3B8' }}>• نظام الموظفين والمشغلين</span>
          </div>
          <h1 style={{ fontSize: '1.75rem', fontWeight: '800', margin: '0 0 0.5rem 0' }}>
            إدارة حسابات الموظفين والتشغيل (Staff & Operators)
          </h1>
          <p style={{ color: '#94A3B8', fontSize: '0.95rem', margin: 0, maxWidth: '720px', lineHeight: '1.6' }}>
            إنشاء حسابات مخصصة لموظفي التشغيل والمشرفين اليوميين بصلاحيات تشغيلية محددة (إدارة الحجوزات، مواعيد الرحلات، شؤون الطلاب، الأسطول) مع إخفاء الأقسام المالية والحساسة تماماً.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <button
            onClick={() => loadStaff(true)}
            disabled={isLoading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              color: '#fff',
              padding: '10px 16px',
              borderRadius: '12px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '14px',
            }}
          >
            <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
            <span>تحديث</span>
          </button>

          <button
            onClick={() => setIsAddModalOpen(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: '#E5232A',
              color: '#fff',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '12px',
              cursor: 'pointer',
              fontWeight: '700',
              fontSize: '14px',
              boxShadow: '0 4px 14px rgba(229, 35, 42, 0.4)',
            }}
          >
            <UserPlus size={18} />
            <span>إضافة موظف تشغيل جديد</span>
          </button>
        </div>
      </div>

      {/* Permission Scope Notice */}
      <div
        style={{
          background: '#EFF6FF',
          border: '1px solid #BFDBFE',
          borderRadius: '16px',
          padding: '1.25rem',
          marginBottom: '2rem',
          display: 'flex',
          gap: '1rem',
          alignItems: 'flex-start',
        }}
      >
        <div
          style={{
            background: '#3B82F6',
            color: '#fff',
            borderRadius: '10px',
            padding: '8px',
            display: 'flex',
          }}
        >
          <ShieldAlert size={20} />
        </div>
        <div style={{ fontSize: '0.9rem', color: '#1E3A8A', lineHeight: '1.6' }}>
          <strong style={{ fontSize: '0.95rem', display: 'block', marginBottom: '4px' }}>
            قواعد الصلاحيات المطبقة على حساب موظف التشغيل (OPERATOR):
          </strong>
          <ul style={{ margin: 0, paddingRight: '1.2rem' }}>
            <li>
              <strong>الأقسام المتاحة للموظف:</strong> لوحة القيادة (المؤشرات التشغيلية)، الحجوزات والكلندر، جدول الرحلات، الطلاب المسجلين (تعديل المسار والمحطة فقط بدون حذف)، إنشاء حسابات الطلاب عبر الواتساب، طلبات الرحلات، الأسطول، الجامعات.
            </li>
            <li>
              <strong>الأقسام المحجوبة والمشفرة:</strong> الخزينة والتعاملات المالية، اعتماد المدفوعات والتحويلات، إعدادات وسائل الدفع، سجل الرقابة والتدقيق، الإعدادات العامة، وإدارة حسابات الموظفين.
            </li>
          </ul>
        </div>
      </div>

      {/* Controls & Search */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '1rem',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '1.5rem',
          background: '#fff',
          padding: '1rem 1.25rem',
          borderRadius: '14px',
          border: '1px solid #E2E8F0',
        }}
      >
        {/* Search */}
        <div style={{ position: 'relative', flex: '1', minWidth: '260px' }}>
          <Search
            size={18}
            style={{
              position: 'absolute',
              right: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#94A3B8',
            }}
          />
          <input
            type="text"
            placeholder="بحث بالاسم أو اسم المستخدم أو الهاتف أو البريد..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 38px 10px 12px',
              borderRadius: '10px',
              border: '1px solid #CBD5E1',
              fontSize: '14px',
              outline: 'none',
            }}
          />
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            style={{
              padding: '9px 14px',
              borderRadius: '10px',
              border: '1px solid #CBD5E1',
              fontSize: '13px',
              background: '#F8FAFC',
              fontWeight: '600',
              color: '#334155',
            }}
          >
            <option value="ALL">جميع الرتب والصلاحيات</option>
            <option value="OPERATOR">موظف تشغيل فقط</option>
            <option value="SUPER_ADMIN">مدير عام النظام</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: '9px 14px',
              borderRadius: '10px',
              border: '1px solid #CBD5E1',
              fontSize: '13px',
              background: '#F8FAFC',
              fontWeight: '600',
              color: '#334155',
            }}
          >
            <option value="ALL">جميع الحالات</option>
            <option value="ACTIVE">الحسابات النشطة</option>
            <option value="SUSPENDED">الحسابات المجمدة</option>
          </select>
        </div>
      </div>

      {/* Staff Table */}
      <div
        style={{
          background: '#fff',
          borderRadius: '16px',
          border: '1px solid #E2E8F0',
          overflow: 'hidden',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
        }}
      >
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'right' }}>
            <thead>
              <tr style={{ background: '#F8FAFC', borderBottom: '1px solid #E2E8F0', color: '#475569', fontSize: '13px' }}>
                <th style={{ padding: '14px 16px', fontWeight: '700' }}>الموظف / الاسم</th>
                <th style={{ padding: '14px 16px', fontWeight: '700' }}>اسم الدخول (Username)</th>
                <th style={{ padding: '14px 16px', fontWeight: '700' }}>الهاتف والبريد</th>
                <th style={{ padding: '14px 16px', fontWeight: '700' }}>الرتبة والصلاحية</th>
                <th style={{ padding: '14px 16px', fontWeight: '700' }}>الحالة</th>
                <th style={{ padding: '14px 16px', fontWeight: '700' }}>آخر تسجيل</th>
                <th style={{ padding: '14px 16px', fontWeight: '700', textAlign: 'center' }}>الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={7} style={{ padding: '3rem', textAlign: 'center', color: '#64748B' }}>
                    <RefreshCw size={24} className="animate-spin" style={{ margin: '0 auto 8px auto' }} />
                    <p>جاري تحميل قائمة الموظفين...</p>
                  </td>
                </tr>
              ) : filteredStaff.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding: '3rem', textAlign: 'center', color: '#64748B' }}>
                    <Users size={36} style={{ color: '#CBD5E1', margin: '0 auto 12px auto' }} />
                    <h3 style={{ fontSize: '1.1rem', color: '#1E293B', marginBottom: '4px' }}>
                      لم يتم العثور على موظفين
                    </h3>
                    <p style={{ fontSize: '0.9rem' }}>
                      يمكنك إنشاء حساب جديد لموظف التشغيل عبر زر الإضافة أعلاه.
                    </p>
                  </td>
                </tr>
              ) : (
                visibleStaff.map((staff) => {
                  const isSuspended = staff.status === 'SUSPENDED';
                  const isSuper = staff.role === 'SUPER_ADMIN';

                  return (
                    <tr
                      key={staff.uid}
                      style={{
                        borderBottom: '1px solid #F1F5F9',
                        transition: 'background 0.15s ease',
                        background: isSuspended ? '#FFF1F2' : '#fff',
                      }}
                    >
                      {/* Name */}
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div
                            style={{
                              width: '38px',
                              height: '38px',
                              borderRadius: '10px',
                              background: isSuper ? '#FEF2F2' : '#F0FDF4',
                              color: isSuper ? '#E5232A' : '#16A34A',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: '700',
                              fontSize: '15px',
                            }}
                          >
                            {staff.fullName.charAt(0)}
                          </div>
                          <div>
                            <strong style={{ display: 'block', fontSize: '14px', color: '#0F172A' }}>
                              {staff.fullName}
                            </strong>
                            <span style={{ fontSize: '11px', color: '#64748B' }}>
                              أُنشئ بواسطة: {staff.createdBy || 'الإدارة'}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Username */}
                      <td style={{ padding: '14px 16px' }}>
                        <code
                          style={{
                            background: '#F1F5F9',
                            padding: '3px 8px',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#0F172A',
                          }}
                        >
                          {staff.username}
                        </code>
                      </td>

                      {/* Phone & Email */}
                      <td style={{ padding: '14px 16px', fontSize: '13px' }}>
                        {staff.phoneNumber ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#334155' }}>
                            <Phone size={13} style={{ color: '#16A34A' }} />
                            <span dir="ltr">{staff.phoneNumber}</span>
                          </div>
                        ) : null}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#64748B', marginTop: '3px' }}>
                          <Mail size={13} />
                          <span dir="ltr">{staff.email}</span>
                        </div>
                      </td>

                      {/* Role & Permissions Badge */}
                      <td style={{ padding: '14px 16px' }}>
                        {isSuper ? (
                          <div>
                            <span
                              style={{
                                background: '#FEE2E2',
                                color: '#991B1B',
                                padding: '4px 10px',
                                borderRadius: '8px',
                                fontSize: '12px',
                                fontWeight: '700',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                marginBottom: '4px',
                              }}
                            >
                              <ShieldCheck size={13} />
                              مدير عام المنظومة
                            </span>
                            <span
                              style={{
                                display: 'block',
                                fontSize: '11px',
                                color: '#16A34A',
                                fontWeight: '600',
                              }}
                            >
                              ✓ كامل الصلاحيات (15/15 قسم)
                            </span>
                          </div>
                        ) : (
                          <div>
                            <span
                              style={{
                                background: '#E0F2FE',
                                color: '#0369A1',
                                padding: '4px 10px',
                                borderRadius: '8px',
                                fontSize: '12px',
                                fontWeight: '700',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                marginBottom: '6px',
                              }}
                            >
                              <Users size={13} />
                              موظف تشغيل
                            </span>
                            <div>
                              <button
                                onClick={() => handleOpenPermissionsModal(staff)}
                                title="التحكم في فتح وقفل أقسام لوحة التحكم لهذا الموظف"
                                style={{
                                  background: '#F0FDF4',
                                  border: '1px solid #BBF7D0',
                                  color: '#15803D',
                                  padding: '4px 8px',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '11px',
                                  fontWeight: '700',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                }}
                              >
                                <Settings size={12} />
                                <span>
                                  صلاحيات الأقسام ({staff.allowedSections ? staff.allowedSections.length : DEFAULT_OPERATOR_SECTIONS.length}/{ALL_SYSTEM_SECTIONS.length})
                                </span>
                              </button>
                            </div>
                          </div>
                        )}
                      </td>

                      {/* Status */}
                      <td style={{ padding: '14px 16px' }}>
                        {isSuspended ? (
                          <span
                            style={{
                              background: '#FFE4E6',
                              color: '#E11D48',
                              padding: '3px 8px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: '700',
                            }}
                          >
                            مجمد مؤقتاً
                          </span>
                        ) : (
                          <span
                            style={{
                              background: '#DCFCE7',
                              color: '#15803D',
                              padding: '3px 8px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: '700',
                            }}
                          >
                            نشط ويعمل
                          </span>
                        )}
                      </td>

                      {/* Last Login */}
                      <td style={{ padding: '14px 16px', fontSize: '12px', color: '#64748B' }}>
                        {staff.lastLoginAt ? (
                          <span>
                            {new Date(staff.lastLoginAt).toLocaleDateString('ar-EG', {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </span>
                        ) : (
                          <span style={{ color: '#94A3B8' }}>لم يسجل دخول بعد</span>
                        )}
                      </td>

                      {/* Actions */}
                      <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                          {/* Permissions Config Button */}
                          <button
                            onClick={() => handleOpenPermissionsModal(staff)}
                            title="تعديل وفتح/قفل الأقسام المخصصة لهذا الموظف"
                            style={{
                              background: '#EFF6FF',
                              border: '1px solid #BFDBFE',
                              color: '#1D4ED8',
                              padding: '6px 10px',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              fontSize: '12px',
                              fontWeight: '600',
                            }}
                          >
                            <Settings size={13} />
                            <span>الأقسام</span>
                          </button>

                          {/* Copy WhatsApp Handover */}
                          <button
                            onClick={() => handleCopyWhatsAppMessage(staff)}
                            title="نسخ بيانات الدخول للواتساب"
                            style={{
                              background: '#F0FDF4',
                              border: '1px solid #BBF7D0',
                              color: '#16A34A',
                              padding: '6px 10px',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              fontSize: '12px',
                              fontWeight: '600',
                            }}
                          >
                            <Copy size={13} />
                            <span>واتساب</span>
                          </button>

                          {/* Reset Password */}
                          <button
                            onClick={() => {
                              setEditingStaff(staff);
                              setNewPasswordVal(staff.password || '');
                              setIsResetModalOpen(true);
                            }}
                            title="تغيير كلمة المرور"
                            style={{
                              background: '#F8FAFC',
                              border: '1px solid #E2E8F0',
                              color: '#475569',
                              padding: '6px 10px',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              fontSize: '12px',
                            }}
                          >
                            <KeyRound size={13} />
                            <span>الباسورد</span>
                          </button>

                          {/* Toggle Suspend / Activate */}
                          {!isSuper && (
                            <button
                              onClick={() => handleToggleStatus(staff)}
                              title={isSuspended ? 'إلغاء التجميد وتنشيط الحساب' : 'تجميد الحساب'}
                              style={{
                                background: isSuspended ? '#FEF2F2' : '#F1F5F9',
                                border: '1px solid',
                                borderColor: isSuspended ? '#FECACA' : '#E2E8F0',
                                color: isSuspended ? '#DC2626' : '#64748B',
                                padding: '6px 10px',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontWeight: '600',
                              }}
                            >
                              {isSuspended ? 'تنشيط' : 'تجميد'}
                            </button>
                          )}

                          {/* Delete */}
                          {!isSuper && (
                            <button
                              onClick={() => handleDeleteStaff(staff)}
                              title="حذف الحساب نهائياً"
                              style={{

                                background: 'transparent',
                                border: 'none',
                                color: '#94A3B8',
                                padding: '6px',
                                cursor: 'pointer',
                                borderRadius: '6px',
                              }}
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          {filteredStaff.length > 0 && (
            <AdminLoadMore
              currentCount={Math.min(visibleCount, filteredStaff.length)}
              totalCount={filteredStaff.length}
              itemName="موظف"
              onLoadMore={() => setVisibleCount((prev) => prev + 10)}
              step={10}
              onShowAll={() => setVisibleCount(filteredStaff.length)}
            />
          )}
        </div>
      </div>

      {/* CREATE STAFF MODAL */}
      {isAddModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '1rem',
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '20px',
              maxWidth: '560px',
              width: '100%',
              padding: '2rem',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              position: 'relative',
            }}
          >
            {/* Close Button */}
            <button
              onClick={() => setIsAddModalOpen(false)}
              style={{
                position: 'absolute',
                top: '18px',
                left: '18px',
                background: '#F1F5F9',
                border: 'none',
                borderRadius: '8px',
                padding: '6px',
                cursor: 'pointer',
                color: '#64748B',
              }}
            >
              <X size={18} />
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1.25rem' }}>
              <div
                style={{
                  background: '#FEE2E2',
                  color: '#E5232A',
                  padding: '10px',
                  borderRadius: '12px',
                  display: 'flex',
                }}
              >
                <UserPlus size={22} />
              </div>
              <div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: '800', margin: 0, color: '#0F172A' }}>
                  إنشاء حساب موظف تشغيل جديد
                </h3>
                <p style={{ margin: 0, fontSize: '13px', color: '#64748B' }}>
                  سيتم منح الموظف صلاحيات تشغيلية فقط للدخول إلى لوحة التحكم
                </p>
              </div>
            </div>

            <form onSubmit={handleCreateStaff}>
              {/* Full Name */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', marginBottom: '6px', color: '#334155' }}>
                  اسم الموظف بالكامل *
                </label>
                <input
                  type="text"
                  required
                  placeholder="مثال: أحمد مصطفى عبد السلام"
                  value={newStaff.fullName}
                  onChange={(e) => setNewStaff({ ...newStaff, fullName: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '10px',
                    border: '1px solid #CBD5E1',
                    fontSize: '14px',
                  }}
                />
              </div>

              {/* Username (Login) */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', marginBottom: '6px', color: '#334155' }}>
                  اسم المستخدم لتسجيل الدخول (بالإنجليزي بدون مسافات) *
                </label>
                <input
                  type="text"
                  required
                  dir="ltr"
                  placeholder="ahmed_ops أو ahmed2026"
                  value={newStaff.username}
                  onChange={(e) => setNewStaff({ ...newStaff, username: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '10px',
                    border: '1px solid #CBD5E1',
                    fontSize: '14px',
                    textAlign: 'left',
                  }}
                />
                <span style={{ fontSize: '11px', color: '#64748B', display: 'block', marginTop: '4px' }}>
                  سيسجل الموظف الدخول باستخدام هذا الاسم مباشرة وكلمة المرور أدناه
                </span>
              </div>

              {/* Phone */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', marginBottom: '6px', color: '#334155' }}>
                  رقم الهاتف / الواتساب
                </label>
                <input
                  type="text"
                  dir="ltr"
                  placeholder="010XXXXXXXX"
                  value={newStaff.phoneNumber}
                  onChange={(e) => setNewStaff({ ...newStaff, phoneNumber: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '10px',
                    border: '1px solid #CBD5E1',
                    fontSize: '14px',
                    textAlign: 'left',
                  }}
                />
              </div>

              {/* Password */}
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', marginBottom: '6px', color: '#334155' }}>
                  كلمة المرور المؤقتة *
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    dir="ltr"
                    required
                    placeholder="OtoStaff@2026"
                    value={newStaff.password}
                    onChange={(e) => setNewStaff({ ...newStaff, password: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 40px 10px 14px',
                      borderRadius: '10px',
                      border: '1px solid #CBD5E1',
                      fontSize: '14px',
                      textAlign: 'left',
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#64748B',
                    }}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* Scope Box */}
              <div
                style={{
                  background: '#F8FAFC',
                  border: '1px solid #E2E8F0',
                  borderRadius: '12px',
                  padding: '12px',
                  marginBottom: '1.5rem',
                  fontSize: '12px',
                  color: '#475569',
                }}
              >
                <span style={{ fontWeight: '700', color: '#0F172A', display: 'block', marginBottom: '4px' }}>
                  🔒 نطاق الصلاحيات التلقائي لموظف التشغيل:
                </span>
                ✅ متاح: متابعة الحجوزات، مواعيد الرحلات، شؤون الطلاب، أسطول السيارات.<br />
                ❌ محجوب تلقائياً: الخزينة، إعدادات الدفع، سجل التدقيق، الإعدادات العامة.
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  style={{
                    padding: '10px 18px',
                    borderRadius: '10px',
                    border: '1px solid #CBD5E1',
                    background: '#fff',
                    color: '#475569',
                    fontSize: '14px',
                    cursor: 'pointer',
                    fontWeight: '600',
                  }}
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    padding: '10px 24px',
                    borderRadius: '10px',
                    border: 'none',
                    background: '#E5232A',
                    color: '#fff',
                    fontSize: '14px',
                    cursor: 'pointer',
                    fontWeight: '700',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <RefreshCw size={16} className="animate-spin" />
                      <span>جاري الإنشاء...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle2 size={16} />
                      <span>تأكيد إنشاء الحساب</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* RESET PASSWORD MODAL */}
      {isResetModalOpen && editingStaff && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '1rem',
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '20px',
              maxWidth: '440px',
              width: '100%',
              padding: '1.75rem',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              position: 'relative',
            }}
          >
            <button
              onClick={() => setIsResetModalOpen(false)}
              style={{
                position: 'absolute',
                top: '16px',
                left: '16px',
                background: '#F1F5F9',
                border: 'none',
                borderRadius: '8px',
                padding: '6px',
                cursor: 'pointer',
                color: '#64748B',
              }}
            >
              <X size={18} />
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1.25rem' }}>
              <div
                style={{
                  background: '#FEF3C7',
                  color: '#D97706',
                  padding: '10px',
                  borderRadius: '12px',
                  display: 'flex',
                }}
              >
                <KeyRound size={20} />
              </div>
              <div>
                <h3 style={{ fontSize: '1.15rem', fontWeight: '800', margin: 0, color: '#0F172A' }}>
                  تغيير كلمة المرور
                </h3>
                <p style={{ margin: 0, fontSize: '13px', color: '#64748B' }}>
                  للموظف: <strong>{editingStaff.fullName}</strong>
                </p>
              </div>
            </div>

            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', marginBottom: '6px', color: '#334155' }}>
                كلمة المرور الجديدة
              </label>
              <input
                type="text"
                dir="ltr"
                placeholder="أدخل كلمة مرور جديدة"
                value={newPasswordVal}
                onChange={(e) => setNewPasswordVal(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  borderRadius: '10px',
                  border: '1px solid #CBD5E1',
                  fontSize: '14px',
                  textAlign: 'left',
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setIsResetModalOpen(false)}
                style={{
                  padding: '9px 16px',
                  borderRadius: '10px',
                  border: '1px solid #CBD5E1',
                  background: '#fff',
                  color: '#475569',
                  fontSize: '13px',
                  cursor: 'pointer',
                  fontWeight: '600',
                }}
              >
                إلغاء
              </button>
              <button
                type="button"
                onClick={handleSaveNewPassword}
                style={{
                  padding: '9px 20px',
                  borderRadius: '10px',
                  border: 'none',
                  background: '#16A34A',
                  color: '#fff',
                  fontSize: '13px',
                  cursor: 'pointer',
                  fontWeight: '700',
                }}
              >
                حفظ كلمة المرور
              </button>
            </div>
          </div>
        </div>
      )}

      {/* INDIVIDUAL SECTION PERMISSIONS MODAL */}
      {isPermissionsModalOpen && permissionStaff && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.7)',
            backdropFilter: 'blur(5px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '1.25rem',
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '24px',
              maxWidth: '820px',
              width: '100%',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 60px -15px rgba(0, 0, 0, 0.35)',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: '1.5rem 2rem',
                borderBottom: '1px solid #E2E8F0',
                background: '#F8FAFC',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '1rem',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div
                  style={{
                    background: '#EFF6FF',
                    color: '#2563EB',
                    padding: '12px',
                    borderRadius: '14px',
                    display: 'flex',
                  }}
                >
                  <Settings size={22} />
                </div>
                <div>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: '800', margin: 0, color: '#0F172A' }}>
                    صلاحيات فتح وقفل الأقسام للموظف
                  </h3>
                  <p style={{ margin: '3px 0 0 0', fontSize: '13px', color: '#64748B' }}>
                    الموظف:{' '}
                    <strong style={{ color: '#0F172A' }}>{permissionStaff.fullName}</strong>{' '}
                    <code style={{ background: '#E2E8F0', padding: '2px 6px', borderRadius: '4px', fontSize: '12px' }}>
                      @{permissionStaff.username}
                    </code>
                  </p>
                </div>
              </div>

              <button
                onClick={() => setIsPermissionsModalOpen(false)}
                style={{
                  background: '#E2E8F0',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '7px',
                  cursor: 'pointer',
                  color: '#475569',
                  display: 'flex',
                }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Quick Actions & Statistics Bar */}
            <div
              style={{
                padding: '1rem 2rem',
                background: '#F1F5F9',
                borderBottom: '1px solid #E2E8F0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '0.75rem',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span
                  style={{
                    background: selectedSections.length > 0 ? '#DCFCE7' : '#FEE2E2',
                    color: selectedSections.length > 0 ? '#15803D' : '#991B1B',
                    padding: '5px 12px',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: '700',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px',
                  }}
                >
                  <Check size={14} />
                  الأقسام المفتوحة: {selectedSections.length} من {ALL_SYSTEM_SECTIONS.length}
                </span>
                <span style={{ fontSize: '12px', color: '#64748B' }}>
                  ({Math.round((selectedSections.length / ALL_SYSTEM_SECTIONS.length) * 100)}% من النظام)
                </span>
              </div>

              {/* Quick Presets */}
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={handleSelectAllSections}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '8px',
                    border: '1px solid #CBD5E1',
                    background: '#fff',
                    color: '#334155',
                    fontSize: '12px',
                    cursor: 'pointer',
                    fontWeight: '600',
                  }}
                >
                  تحديد الكل (15)
                </button>
                <button
                  type="button"
                  onClick={handleSetDefaultOperatorSections}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '8px',
                    border: '1px solid #BFDBFE',
                    background: '#EFF6FF',
                    color: '#1D4ED8',
                    fontSize: '12px',
                    cursor: 'pointer',
                    fontWeight: '700',
                  }}
                >
                  صلاحيات التشغيل الافتراضية (9)
                </button>
                <button
                  type="button"
                  onClick={handleClearAllSections}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '8px',
                    border: '1px solid #FECACA',
                    background: '#FEF2F2',
                    color: '#DC2626',
                    fontSize: '12px',
                    cursor: 'pointer',
                    fontWeight: '600',
                  }}
                >
                  إلغاء الكل (قفل تام)
                </button>
              </div>
            </div>

            {/* Scrollable Body: Grouped Sections */}
            <div
              style={{
                padding: '1.5rem 2rem',
                overflowY: 'auto',
                flex: 1,
              }}
            >
              {(
                [
                  { category: 'OPERATIONAL', title: '1. العمليات والتشغيل اليومي' },
                  { category: 'STUDENTS', title: '2. شؤون الطلاب والحسابات' },
                  { category: 'FLEET', title: '3. الأسطول والجامعات' },
                  { category: 'FINANCIAL', title: '4. المالية والخزينة (حساسة)' },
                  { category: 'SECURITY', title: '5. إعدادات النظام والرقابة' },
                ] as const
              ).map((group) => {
                const groupSections = ALL_SYSTEM_SECTIONS.filter(
                  (s) => s.category === group.category
                );
                if (groupSections.length === 0) return null;

                const allInGroupSelected = groupSections.every((s) =>
                  selectedSections.includes(s.key)
                );

                return (
                  <div key={group.category} style={{ marginBottom: '1.75rem' }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '0.75rem',
                        borderBottom: '1px solid #E2E8F0',
                        paddingBottom: '0.5rem',
                      }}
                    >
                      <h4
                        style={{
                          margin: 0,
                          fontSize: '14px',
                          fontWeight: '800',
                          color: group.category === 'FINANCIAL' ? '#B91C1C' : '#1E293B',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                      >
                        {group.title}
                        {group.category === 'FINANCIAL' && (
                          <span
                            style={{
                              background: '#FEE2E2',
                              color: '#991B1B',
                              fontSize: '10px',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontWeight: '700',
                            }}
                          >
                            خزينة وأموال
                          </span>
                        )}
                      </h4>

                      <button
                        type="button"
                        onClick={() => {
                          if (allInGroupSelected) {
                            setSelectedSections((prev) =>
                              prev.filter((k) => !groupSections.some((s) => s.key === k))
                            );
                          } else {
                            const toAdd = groupSections.map((s) => s.key);
                            setSelectedSections((prev) => Array.from(new Set([...prev, ...toAdd])));
                          }
                        }}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#2563EB',
                          fontSize: '12px',
                          cursor: 'pointer',
                          fontWeight: '600',
                        }}
                      >
                        {allInGroupSelected ? 'إلغاء تحديد المجموعة' : 'تحديد المجموعة بالكامل'}
                      </button>
                    </div>

                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                        gap: '0.75rem',
                      }}
                    >
                      {groupSections.map((section) => {
                        const isChecked = selectedSections.includes(section.key);

                        return (
                          <div
                            key={section.key}
                            onClick={() => handleToggleSection(section.key)}
                            style={{
                              padding: '12px 14px',
                              borderRadius: '12px',
                              border: '1.5px solid',
                              borderColor: isChecked ? '#16A34A' : '#E2E8F0',
                              background: isChecked ? '#F0FDF4' : '#FAFAFA',
                              cursor: 'pointer',
                              transition: 'all 0.15s ease',
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '12px',
                            }}
                          >
                            {/* Checkbox switch */}
                            <div
                              style={{
                                width: '22px',
                                height: '22px',
                                borderRadius: '6px',
                                border: '2px solid',
                                borderColor: isChecked ? '#16A34A' : '#CBD5E1',
                                background: isChecked ? '#16A34A' : '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                marginTop: '2px',
                                transition: 'all 0.15s ease',
                              }}
                            >
                              {isChecked && <Check size={14} color="#fff" strokeWidth={3} />}
                            </div>

                            <div style={{ flex: 1 }}>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  gap: '8px',
                                  marginBottom: '3px',
                                }}
                              >
                                <strong
                                  style={{
                                    fontSize: '13px',
                                    color: isChecked ? '#0F172A' : '#64748B',
                                  }}
                                >
                                  {section.name}
                                </strong>
                                <span
                                  style={{
                                    fontSize: '11px',
                                    fontWeight: '700',
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    background: isChecked ? '#DCFCE7' : '#F1F5F9',
                                    color: isChecked ? '#15803D' : '#94A3B8',
                                  }}
                                >
                                  {isChecked ? 'مفتوح للموظف' : 'مغلق ومحجوب'}
                                </span>
                              </div>
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: '11.5px',
                                  color: '#64748B',
                                  lineHeight: '1.4',
                                }}
                              >
                                {section.description}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Modal Footer */}
            <div
              style={{
                padding: '1.25rem 2rem',
                borderTop: '1px solid #E2E8F0',
                background: '#F8FAFC',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div style={{ fontSize: '13px', color: '#64748B' }}>
                سيتم تحديث صلاحيات الحساب فوراً وسيرى الموظف الأقسام المحددة فقط.
              </div>

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  type="button"
                  onClick={() => setIsPermissionsModalOpen(false)}
                  style={{
                    padding: '10px 18px',
                    borderRadius: '10px',
                    border: '1px solid #CBD5E1',
                    background: '#fff',
                    color: '#475569',
                    fontSize: '13px',
                    cursor: 'pointer',
                    fontWeight: '600',
                  }}
                >
                  إلغاء
                </button>
                <button
                  type="button"
                  disabled={isSavingPermissions}
                  onClick={handleSavePermissions}
                  style={{
                    padding: '10px 24px',
                    borderRadius: '10px',
                    border: 'none',
                    background: '#2563EB',
                    color: '#fff',
                    fontSize: '13px',
                    cursor: 'pointer',
                    fontWeight: '700',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
                  }}
                >
                  {isSavingPermissions ? (
                    <>
                      <RefreshCw size={15} className="animate-spin" />
                      <span>جاري حفظ الصلاحيات...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle2 size={16} />
                      <span>حفظ الصلاحيات وتطبيقها</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminStaffManagement;

